import { LANGS, LANG_LABEL, useLang } from "../lib/i18n";
import { lpCopy } from "../lib/i18n/landing";
import { isNativePlatform } from "../lib/native";
import { SytoLogo } from "./ui/syto-logo";

type Props = {
  // Code stored from /r/<code> URL. Null when user landed without a referral.
  referralCode: string | null;
  onSignIn: () => void;
  onSignUp: () => void;
  onOpenPrivacy: () => void;
  onOpenTerms: () => void;
};

const ACCENT = "var(--accent)";

export function LandingPage({
  referralCode,
  onSignIn,
  onSignUp,
  onOpenPrivacy,
  onOpenTerms,
}: Props) {
  const { lang, setLang } = useLang();
  const c = lpCopy[lang];

  return (
    <main
      className="w-full"
      style={{ background: "var(--bg)", color: "var(--ink)", minHeight: "100dvh" }}
    >
      {/* === NAV === */}
      <nav
        className="sticky top-0 z-30"
        style={{
          // var(--nav-bg) flips to a dark translucent in dark mode; the
          // hardcoded light cream that was here made the nav unreadable
          // (white-on-light) for visitors with the OS in dark mode.
          background: "var(--nav-bg)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--nav-border)",
          // iOS notch / Dynamic Island: lift the sticky nav clear of the
          // status bar. env() resolves to 0 on web/Android so max(14px, 0)
          // collapses to the previous 14px exactly — no visual regression
          // off iOS. Applied on the <nav> itself rather than the inner row
          // because the row's flex centering must not be re-offset.
          paddingTop: "max(14px, env(safe-area-inset-top))",
          paddingBottom: 14,
        }}
      >
        <div
          className="mx-auto flex items-center justify-between"
          style={{ maxWidth: 1200, padding: "0 22px" }}
        >
          <SytoLogo size={22} />
          <div className="flex items-center gap-3">
            <LangSwitcher current={lang} onPick={setLang} />
            <button
              type="button"
              onClick={onSignIn}
              className="text-[13px] font-semibold"
              style={{ color: "var(--ink-2)" }}
            >
              {c.navSignIn}
            </button>
            <button
              type="button"
              onClick={onSignUp}
              className="text-[13px] font-bold px-4 py-2 rounded-full"
              style={{ background: "var(--ink)", color: "var(--bg)" }}
            >
              {c.navSignUp} →
            </button>
          </div>
        </div>
      </nav>

      {/* === REFERRAL BANNER === */}
      {referralCode ? (
        <div
          style={{
            background: "var(--accent)",
            color: "var(--accent-ink)",
            textAlign: "center",
            padding: "8px 22px",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {c.referralBanner(referralCode)}
        </div>
      ) : null}

      {/* === HERO === */}
      <section className="mx-auto" style={{ maxWidth: 1200, padding: "56px 22px 40px" }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.2em",
            color: ACCENT,
            fontWeight: 700,
            textTransform: "uppercase",
          }}
        >
          {c.heroEyebrow}
        </div>

        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(40px, 7vw, 84px)",
            fontWeight: 800,
            letterSpacing: "-0.05em",
            lineHeight: 0.95,
            marginTop: 14,
            color: "var(--ink)",
          }}
        >
          {c.heroLine1}
          <br />
          {c.heroLine2}
          <br />
          <span style={{ color: ACCENT }}>
            {c.heroLine3}
            <span style={{ color: ACCENT }}>.</span>
          </span>
        </h1>

        <p
          style={{
            fontSize: 18,
            lineHeight: 1.5,
            color: "var(--ink-2)",
            maxWidth: 620,
            marginTop: 22,
          }}
        >
          {c.heroSub}
        </p>

        <div className="flex flex-wrap gap-3" style={{ marginTop: 28 }}>
          <button
            type="button"
            onClick={onSignUp}
            className="text-[15px] font-bold uppercase tracking-wide"
            style={{
              padding: "18px 28px",
              borderRadius: 14,
              background: "var(--accent)",
              color: "var(--accent-ink)",
              boxShadow: "var(--shadow-cta)",
            }}
          >
            {c.ctaPrimary}
          </button>
          <button
            type="button"
            onClick={onSignIn}
            className="text-[15px] font-semibold"
            style={{
              padding: "18px 28px",
              borderRadius: 14,
              background: "transparent",
              color: "var(--ink)",
              // var(--ink-2) reads at ~0.65 alpha against the page bg in
              // both themes, so the outline is unmistakably "interactive".
              // The old 1px nav-border (0.07 alpha in dark) looked disabled.
              boxShadow: "inset 0 0 0 1.5px var(--ink-2)",
            }}
          >
            {c.ctaSecondary}
          </button>
        </div>

        <p
          style={{
            fontSize: 12,
            color: "var(--muted)",
            marginTop: 14,
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.04em",
          }}
        >
          {c.heroFinePrint}
        </p>

        {/* === Video === Skipped inside the Capacitor APK: hero.mp4 is
            stripped from the native bundle (it is 15 MB of marketing video
            the signed-in app never needs, scripts/prepare-capacitor-assets.mjs).
            Web LP keeps it unchanged. */}
        {isNativePlatform() ? null : (
          <div
            style={{
              marginTop: 48,
              borderRadius: 28,
              overflow: "hidden",
              boxShadow: "0 30px 80px rgba(0, 0, 0, 0.18)",
              background: "#0D0E10",
              aspectRatio: "16 / 9",
              position: "relative",
            }}
          >
            {/* No autoplay — the user presses play themselves, so the video
                starts with sound on. `controls` gives play-pause, volume,
                scrubber and fullscreen. First frame shows as the poster. */}
            <video
              src="/hero.mp4"
              playsInline
              controls
              preload="metadata"
              controlsList="nodownload"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </div>
        )}
      </section>

      {/* === KILLER FEATURES === */}
      <section className="mx-auto" style={{ maxWidth: 1200, padding: "48px 22px" }}>
        <SectionEyebrow>{c.featuresEyebrow}</SectionEyebrow>
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", marginTop: 18 }}
        >
          {c.features.map((f, i) => (
            <FeatureCard
              key={f.title}
              tag={String(i + 1).padStart(2, "0")}
              title={f.title}
              body={f.body}
            />
          ))}
        </div>
      </section>

      {/* === PROBLEM/SOLUTION === */}
      <section className="mx-auto" style={{ maxWidth: 1200, padding: "48px 22px" }}>
        <div
          className="grid gap-6"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}
        >
          <div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                letterSpacing: "0.2em",
                color: "var(--bad-fg)",
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              {c.problemEyebrow}
            </div>
            <ColumnH2>
              {c.problemH2}
              <span style={{ color: ACCENT }}>.</span>
            </ColumnH2>
            <BulletList items={c.problemList} />
          </div>

          <div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                letterSpacing: "0.2em",
                color: "var(--ok-fg)",
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              {c.solutionEyebrow}
            </div>
            <ColumnH2>
              {c.solutionH2}
              <span style={{ color: ACCENT }}>.</span>
            </ColumnH2>
            <BulletList items={c.solutionList} />
          </div>
        </div>
      </section>

      {/* === BETA CTA (pricing TBD) === */}
      <section className="mx-auto" style={{ maxWidth: 1200, padding: "48px 22px" }}>
        <div
          style={{
            background: "var(--surface)",
            borderRadius: 28,
            padding: "48px 32px",
            boxShadow: "var(--shadow-card)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.2em",
              color: ACCENT,
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            {c.betaEyebrow}
          </div>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(32px, 5vw, 52px)",
              fontWeight: 800,
              letterSpacing: "-0.04em",
              lineHeight: 1.05,
              marginTop: 14,
              color: "var(--ink)",
            }}
          >
            {c.betaH2}
            <span style={{ color: ACCENT }}>.</span>
          </h2>
          <p
            style={{
              fontSize: 16,
              lineHeight: 1.5,
              color: "var(--ink-2)",
              maxWidth: 560,
              margin: "16px auto 0",
            }}
          >
            {c.betaBody}
          </p>
          <button
            type="button"
            onClick={onSignUp}
            className="text-[15px] font-bold uppercase tracking-wide"
            style={{
              marginTop: 28,
              padding: "18px 32px",
              borderRadius: 14,
              background: "var(--accent)",
              color: "var(--accent-ink)",
              boxShadow: "var(--shadow-cta)",
            }}
          >
            {c.betaCta}
          </button>
          <p
            style={{
              marginTop: 14,
              fontSize: 12,
              color: "var(--muted)",
              fontFamily: "var(--font-mono)",
              letterSpacing: "0.04em",
            }}
          >
            {c.betaFinePrint}
          </p>
        </div>
      </section>

      {/* === HOW IT WORKS === */}
      <section className="mx-auto" style={{ maxWidth: 1200, padding: "48px 22px" }}>
        <SectionEyebrow>{c.howEyebrow}</SectionEyebrow>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 40,
            fontWeight: 800,
            letterSpacing: "-0.04em",
            lineHeight: 1,
            marginTop: 10,
            color: "var(--ink)",
          }}
        >
          {c.howH2}
          <span style={{ color: ACCENT }}>.</span>
        </h2>
        <ol
          className="grid gap-4"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", marginTop: 28 }}
        >
          {c.steps.map((s, i) => (
            <Step key={s.title} n={String(i + 1)} title={s.title} body={s.body} />
          ))}
        </ol>
      </section>

      {/* === FINAL CTA === */}
      <section className="mx-auto" style={{ maxWidth: 1200, padding: "48px 22px 80px" }}>
        <div
          style={{
            background: "var(--ink)",
            color: "var(--bg)",
            borderRadius: 28,
            padding: "56px 32px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.2em",
              color: ACCENT,
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            {c.finalEyebrow}
          </div>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(36px, 6vw, 64px)",
              fontWeight: 800,
              letterSpacing: "-0.04em",
              lineHeight: 1,
              marginTop: 14,
            }}
          >
            {c.finalLine1}
            <br />
            <span style={{ color: ACCENT }}>
              {c.finalLine2}
              <span style={{ color: ACCENT }}>.</span>
            </span>
          </h2>
          <button
            type="button"
            onClick={onSignUp}
            className="text-[15px] font-bold uppercase tracking-wide"
            style={{
              marginTop: 28,
              padding: "20px 36px",
              borderRadius: 14,
              background: "var(--accent)",
              color: "var(--accent-ink)",
              boxShadow: "var(--shadow-cta)",
            }}
          >
            {c.finalCta}
          </button>
          <p style={{ marginTop: 16, fontSize: 13, color: "rgba(240, 239, 236, 0.6)" }}>
            {c.finalFinePrint}
          </p>
        </div>
      </section>

      {/* === FOOTER === */}
      <footer
        className="mx-auto"
        style={{ maxWidth: 1200, padding: "32px 22px", borderTop: "1px solid var(--nav-border)" }}
      >
        {/* Made-in-Poland — quiet, tasteful, with a small flag mark. */}
        <div className="flex justify-center" style={{ marginBottom: 22 }}>
          <div
            className="flex items-center gap-2.5"
            style={{
              padding: "8px 14px",
              borderRadius: 999,
              background: "var(--surface)",
              boxShadow: "inset 0 0 0 1px var(--nav-border)",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                flexDirection: "column",
                width: 18,
                height: 12,
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 0 0 1px var(--nav-border)",
              }}
            >
              <span style={{ flex: 1, background: "#ffffff" }} />
              <span style={{ flex: 1, background: "#d4213d" }} />
            </span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                letterSpacing: "0.04em",
                color: "var(--ink-2)",
                fontWeight: 600,
              }}
            >
              {c.madeIn}
            </span>
          </div>
        </div>

        <div
          className="flex flex-wrap items-center justify-between gap-4"
          style={{ fontSize: 12, color: "var(--muted)" }}
        >
          <div className="flex items-center gap-2">
            <SytoLogo size={16} />
            <span style={{ fontFamily: "var(--font-mono)" }}>
              © {new Date().getFullYear()} Sytofood
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <a href="/android" className="underline">
              Android
            </a>
            <button type="button" onClick={onOpenPrivacy} className="underline">
              {c.footerPrivacy}
            </button>
            <button type="button" onClick={onOpenTerms} className="underline">
              {c.footerTerms}
            </button>
          </div>
        </div>
      </footer>
    </main>
  );
}

function LangSwitcher({
  current,
  onPick,
}: {
  current: string;
  onPick: (lang: "pl" | "en" | "de") => void;
}) {
  return (
    <div className="flex items-center" style={{ gap: 2 }}>
      {LANGS.map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => onPick(l)}
          aria-pressed={l === current}
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 700,
            padding: "4px 7px",
            borderRadius: 6,
            background: l === current ? "var(--ink)" : "transparent",
            color: l === current ? "var(--bg)" : "var(--muted)",
            border: "none",
            cursor: "pointer",
          }}
        >
          {LANG_LABEL[l]}
        </button>
      ))}
    </div>
  );
}

function SectionEyebrow({ children }: { children: string }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        letterSpacing: "0.2em",
        color: "var(--muted)",
        fontWeight: 600,
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

function ColumnH2({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontFamily: "var(--font-display)",
        fontSize: 32,
        fontWeight: 800,
        letterSpacing: "-0.03em",
        lineHeight: 1.05,
        marginTop: 10,
        color: "var(--ink)",
      }}
    >
      {children}
    </h2>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul
      style={{
        marginTop: 18,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        fontSize: 15,
        color: "var(--ink-2)",
        lineHeight: 1.5,
      }}
    >
      {items.map((it) => (
        <li key={it}>· {it}</li>
      ))}
    </ul>
  );
}

function FeatureCard({ tag, title, body }: { tag: string; title: string; body: string }) {
  return (
    <article
      style={{
        background: "var(--surface)",
        borderRadius: 18,
        padding: 22,
        boxShadow: "var(--shadow-card)",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--accent)",
          fontWeight: 700,
          letterSpacing: "0.1em",
        }}
      >
        / {tag}
      </div>
      <h3
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 22,
          fontWeight: 800,
          letterSpacing: "-0.02em",
          marginTop: 8,
          color: "var(--ink)",
        }}
      >
        {title}
      </h3>
      <p style={{ marginTop: 10, fontSize: 14, lineHeight: 1.55, color: "var(--ink-2)" }}>{body}</p>
    </article>
  );
}

function Step({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <li
      style={{
        background: "var(--surface)",
        borderRadius: 18,
        padding: 22,
        boxShadow: "var(--shadow-card)",
      }}
    >
      <div
        className="tabular"
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 56,
          fontWeight: 800,
          letterSpacing: "-0.05em",
          color: "var(--accent)",
          lineHeight: 1,
        }}
      >
        {n}
      </div>
      <h3
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 20,
          fontWeight: 800,
          letterSpacing: "-0.02em",
          marginTop: 8,
          color: "var(--ink)",
        }}
      >
        {title}
      </h3>
      <p style={{ marginTop: 8, fontSize: 14, lineHeight: 1.5, color: "var(--ink-2)" }}>{body}</p>
    </li>
  );
}
